import { useDocumentsIndexingStore } from '~/stores/documentsIndexing'
import { planAllows } from '~/composables/usePlanFeature'
import { useWorkflowRunsStore } from '~/stores/workflowRuns'
import { useActivityStore } from '~/stores/activity'
import { useActivityRunsStore } from '~/stores/activityRuns'
import type { ActivityFeed } from '~~/shared/activity/activityFeed'

/**
 * The one poller behind the Activity Center — MAR-109, #70.
 *
 * ONE, exactly. `stores/toolRuns.ts` kept a fourth timer against `GET /api/tools/runs` until #70
 * folded tool runs onto this feed; that store and that endpoint are gone, and every kind in the
 * panel now arrives on this tick.
 *
 * Before this, three client timers ran over overlapping data: indexing polled `/api/items/status`
 * every 3s, `useRunsAutoRefresh` polled `/api/runs` every 3s on the runs list, and every chat
 * workflow card ran its own 5s interval against `/api/runs/:id` — which gave up after 24 attempts and
 * declared a still-running workflow failed.
 *
 * Transport lives here; MEANING stays with the producers. This composable never maps a status to a
 * label, a tone or an action — it fetches one payload and hands each half to the store that owns it.
 *
 * Mounted once, in `PanelLayout`, so it covers every `/app` route. The module-level timer makes a
 * second mount harmless: two callers cannot produce two intervals.
 */

const POLL_MS = 3000

let timer: ReturnType<typeof setInterval> | null = null
/** Guards against a second concurrent tick when a slow request overlaps the next interval. */
let inFlight = false

export function useActivityFeed() {
  const { apiFetch } = useApi()
  const indexing = useDocumentsIndexingStore()
  const runs = useWorkflowRunsStore()
  const activity = useActivityStore()
  const runsFeed = useActivityRunsStore()

  function hasPending() {
    // `runsFeed.pending` keeps the timer armed for a contract review or an OCR read this session
    // never started — work the other two producers have no id for, because they only know what was
    // launched from this tab.
    return indexing.pendingIds.length > 0 || runs.pendingRunIds.length > 0 || runsFeed.pending
  }

  async function tick() {
    if (inFlight) return
    inFlight = true
    try {
      // A member without a seat is refused the feed (`403 no_seat`, MAR-1212): nothing of theirs can
      // be running, so the tick asks nothing and disarms the timer.
      if (!(await planAllows('app'))) {
        stop()
        return
      }
      const data = await apiFetch<ActivityFeed>('/api/activity', {
        method: 'POST',
        // `trackedIds`, not `pendingIds`: what must be RESOLVED is wider than what keeps the timer
        // armed. A document waiting on a human decision is asked about on every tick this feed
        // happens to be running, because its finished state is `ready` and discovery drops `ready`.
        body: { ids: indexing.trackedIds, includeDismissed: activity.showDismissed },
      })

      indexing.applyRows(data?.indexing ?? [], data?.missing ?? [])
      await runs.applyActiveRuns((data?.workflows ?? []) as any)
      runsFeed.applyRuns((data?.runs ?? []) as any)
    } catch {
      // Tracked by useApi. The interval stays armed on purpose: a transient failure must not strand
      // the panel showing "procesando" forever.
    } finally {
      inFlight = false
    }

    if (!hasPending()) stop()
  }

  /** Idempotent. Arms the interval; the tick itself disarms it once nothing is pending. */
  function start() {
    if (!import.meta.client || timer) return
    timer = setInterval(tick, POLL_MS)
  }

  /**
   * Read now, then keep watching if the read found work.
   *
   * The three callers that need it are the mount, the "mostrar descartados" toggle and a producer
   * asking for a tick — all of them want the answer immediately rather than up to `POLL_MS` later,
   * and all of them can turn up work that must then be followed to completion.
   */
  async function refreshNow() {
    await tick()
    if (hasPending()) start()
  }

  function stop() {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  /**
   * One fetch on mount is the REHYDRATION: it adopts work this session never tracked — an upload
   * still indexing, a quota block nobody saw, a workflow that was running when the tab was closed.
   * Active state is durable in `items` and `workflow_runs`, so the bell no longer starts empty after
   * a reload. Settled receipts stay session-memory (ACTIVITY_CENTER §6) and are deliberately absent.
   */
  onMounted(refreshNow)

  // Producers ask for a tick when they create work (`indexing.ensurePoll()` from every upload site,
  // `runs.trackRun()` from every launch path). Arming the timer is enough — both producers insert an
  // optimistic row first, so the panel is never briefly empty while the first tick is in flight.
  indexing.onPollRequested(start)
  runs.onPollRequested(start)
  // The runs producer asks for a FETCH, not just a timer: unlike the other two it holds no
  // optimistic row, so a conversion just started — or one already running when the panel was opened
  // — is invisible until a tick has actually happened (#70).
  runsFeed.onPollRequested(() => { void refreshNow() })

  /**
   * "Mostrar descartados" flipped. Fetch immediately rather than waiting up to a tick — and when it
   * goes OFF, purge first: acknowledged rows simply stop arriving in the feed, and reconciliation only
   * removes what the server contradicts, so nothing else would take them off the screen.
   */
  activity.onRefreshRequested(() => {
    if (!activity.showDismissed) {
      indexing.purgeDismissed()
      runs.purgeDismissed()
      runsFeed.purgeDismissed()
    }
    void refreshNow()
  })

  onUnmounted(stop)

  return { start, stop, tick }
}
